import React, { useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { ArrowNextIcon, WarningIcon, SuccessIcon, IconEyeHideS } from '../../../../icons';
import { FormattedMessage } from '../../../../util/reactIntl';
import { Alert, IconSpinner } from '../../../../components';
import { CHECKLIST_ID } from '../../NavigationBar';
import { getSectionCompleteness } from '../../../../util/sectionCompleteness';

import css from '../../ProfileSettingsForm.css';
import sectionCss from './ChecklistSection.css';
import { useParentWidth } from './useParentWidth';
import { useProfileCompleteEvent } from './useProfileCompleteEvent';
import { usePercentages } from './usePercentages';
import { useInitialSectionVisibility } from './useInitialSectionVisibility';

const ChecklistSection = ({
    currentUser,
    userDocuments,
    onUpdateProfile,
    triggerEditSection,
    uploadDocumentsInProgress,
}) => {
    const [circleRef, setRef] = useState(null);
    const parentRef = useRef();

    const {
        id: { uuid: userId },
        attributes: {
            profile: {
                publicData: { profileComplete: PD_profileComplete },
            },
        },
    } = currentUser;

    const { sections, all, mandatory } = getSectionCompleteness(currentUser, userDocuments) || {};

    const [open, setOpen] = useInitialSectionVisibility(PD_profileComplete);
    const parentWidth = useParentWidth(parentRef);
    const { circleLength, percentagePrimary, percentageSecondary } = usePercentages({
        all,
        circleRef,
        parentWidth,
    });

    const noProgress = percentagePrimary === 0 || percentageSecondary === 0;
    const mandatoryFieldsComplete = mandatory === 1;
    const profileComplete = percentagePrimary === 100;
    const dimension = parentWidth >= 330 ? '330' : parentWidth;

    useProfileCompleteEvent({
        onUpdateProfile,
        userId,
        mandatoryFieldsComplete,
        PD_profileComplete,
        profileComplete,
    });

    if (uploadDocumentsInProgress) return <IconSpinner />;

    if (profileComplete) {
        return (
            <Alert
                type="success"
                description="ProfileSettingsForm.profileComplete"
                rootClassName={sectionCss.alert}
            />
        );
    }

    return (
        <div
            className={classNames(css.infoBlock, sectionCss.infoBlock)}
            ref={parentRef}
            id={CHECKLIST_ID}
        >
            <h2 className={sectionCss.heading}>
                <FormattedMessage id="ProfileSettingsForm.checklistHeading" />
            </h2>
            <p className={sectionCss.checklistDataProtectedMsg}>
                <IconEyeHideS />
                <span>Nur für dich sichtbar</span>
            </p>
            <section
                className={sectionCss.checklistHolder}
                style={{ maxHeight: `${dimension / 2 + 50}px` }}
            >
                <svg
                    width={dimension}
                    height={dimension}
                    viewBox={`0 0 ${dimension} ${dimension}`}
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ transform: 'rotate(180deg)' }}
                >
                    <circle
                        ref={setRef}
                        className={sectionCss.transparentCircle}
                        strokeLinecap="round"
                        style={{
                            strokeDasharray: `${circleLength / 2}, ${circleLength / 2}`,
                            strokeDashoffset: 0,
                            ...(circleLength === 1 ? { strokeWidth: 0 } : {}),
                        }}
                        cy={`${dimension / 2}`}
                        cx={`${dimension / 2}`}
                        r={`${dimension / 2 - 10}`}
                    />
                    <circle
                        className={sectionCss.progressCircle}
                        strokeLinecap="round"
                        style={{
                            strokeDasharray: `${percentageSecondary}, ${circleLength -
                                percentageSecondary}`,

                            ...(noProgress ? { display: 'none' } : {}),
                            ...(circleLength === 1 ? { strokeWidth: 0 } : {}),
                        }}
                        cy={`${dimension / 2}`}
                        cx={`${dimension / 2}`}
                        r={`${dimension / 2 - 10}`}
                    />
                </svg>
                <div>{percentagePrimary}%</div>
                <p>0%</p>
                <p>100%</p>
            </section>

            <p className={sectionCss.checklistTasks} onClick={() => setOpen(!open)}>
                <span>Deine Aufgaben</span>
                <ArrowNextIcon rootClassName={open ? sectionCss.open : sectionCss.closed} />
            </p>

            {open && (
                <aside className={sectionCss.checklist}>
                    {sections.map(({ label, completed, mandatory }) => (
                        <p
                            key={label}
                            className={classNames(sectionCss.checklistItem, {
                                [sectionCss.completed]: !!completed,
                            })}
                            onClick={() => {
                                if (!completed) {
                                    triggerEditSection(label);
                                }
                            }}
                        >
                            {completed && <SuccessIcon />}{' '}
                            {!completed && mandatory && <WarningIcon />}
                            {!completed && !mandatory && (
                                <span className={sectionCss.placeholder} />
                            )}
                            <FormattedMessage
                                id={`ProfileSettingsForm.editUserInfoAction-${label}`}
                            />
                        </p>
                    ))}
                </aside>
            )}
        </div>
    );
};

export default ChecklistSection;
